import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import { cached, readCache } from "./offlineCache";
import { SYNCED_EVENT } from "./offlineSync";
import { EXAM_CHANGE_EVENT } from "./activeExam";

export interface CachedState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | Error | null;
  /**
   * When the data shown came from the device rather than the server: the time
   * it was saved. null when it is fresh. Screens show an "offline" note.
   */
  staleSince: number | null;
  reload: () => void;
}

/**
 * Like `useAsync`, but a screen still has something to show offline.
 *
 * The last good answer is shown at once, before the request finishes, so an
 * offline student does not stare at a skeleton waiting for a timeout. When the
 * request succeeds it replaces the saved copy; when the network is gone the
 * saved copy stays on screen with `staleSince` set. A real server error (a
 * 401, a 500) is still an error.
 *
 * Reconnecting reloads, so the page catches up without a manual refresh.
 */
export function useCachedAsync<T>(
  name: string,
  load: () => Promise<T>,
  deps: unknown[] = [],
): CachedState<T> {
  const initial = readCache<T>(name);
  const [data, setData] = useState<T | null>(initial?.data ?? null);
  // Not stale yet: the saved copy is only "offline data" once the network has
  // actually failed. Marking it stale while a refresh is still in flight put
  // the offline note up on every open, and the hub, reading that as offline,
  // hid every subject that was not downloaded until the refresh landed.
  const [staleSince, setStaleSince] = useState<number | null>(null);
  const [loading, setLoading] = useState(initial === null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  useEffect(() => {
    let cancelled = false;
    const saved = readCache<T>(name);
    if (saved) {
      setData(saved.data);
      setLoading(false);
    } else {
      // Nothing saved for this (another subject, another exam): show nothing
      // rather than the previous one's numbers under the new heading.
      setData(null);
      setLoading(true);
    }
    setError(null);
    setStaleSince(null);

    cached(name, run)
      .then((result) => {
        if (cancelled) return;
        setData(result.data);
        setStaleSince(result.savedAt);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught : new Error(String(caught)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `name` changes with the deps that matter (subject id and so on).
  }, [run, name, attempt]);

  // Back online, again once queued answers have reached the server — that is
  // when the readiness shown here has actually moved — and on an exam switch,
  // when everything on screen belongs to the other profile.
  useEffect(() => {
    const again = () => setAttempt((n) => n + 1);
    window.addEventListener("online", again);
    window.addEventListener(SYNCED_EVENT, again);
    window.addEventListener(EXAM_CHANGE_EVENT, again);
    return () => {
      window.removeEventListener("online", again);
      window.removeEventListener(SYNCED_EVENT, again);
      window.removeEventListener(EXAM_CHANGE_EVENT, again);
    };
  }, []);

  // With something to show, a failed refresh is not worth a full-screen error.
  const shown = data !== null && error !== null && isNetwork(error) ? null : error;
  return { data, loading, error: shown, staleSince, reload: () => setAttempt((n) => n + 1) };
}

function isNetwork(error: Error): boolean {
  return error instanceof ApiError && error.status === 0;
}
